import {
  CreateButton,
  DateField,
  DeleteButton,
  FilterDropdown,
  List,
  TextField,
  getDefaultSortOrder,
  useTable,
  useSelect,
} from "@refinedev/antd";

import { Col, Divider, Row, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useModal, useGetIdentity } from "@refinedev/core";

import { IVesselResource, IVesselStartupKit } from "interfaces";
import prettyBytes from "pretty-bytes";
import dayjs from "dayjs";
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from "react";
import { DownloadResource } from "./components/download-resource";

export const VesselStartupKitList: React.FC<IResourceComponentsProps> = () => {
  const [record, setRecord] = React.useState<IVesselStartupKit>();
  const { data: identity } = useGetIdentity();  
  const isVesselUser = identity?.role === "VesselUser";

  // extend dayjs with required plugins 
  dayjs.extend(isoWeek);
  dayjs.extend(utc);
  dayjs.extend(timezone);

  // Function to get the display week where Thursday to next Wednesday is considered as the current ISO week
  const getDisplayWeekNumber = () => {
    // Get current date in IST timezone
    const now = dayjs().tz("Asia/Kolkata");
    
    // Find the most recent Thursday (or today if it's Thursday)
    // This is the start of our display week
    let thursdayOfWeek;
    const dayOfWeek = now.day(); // 0 is Sunday, 4 is Thursday
    
    if (dayOfWeek === 4) {
      // Today is Thursday
      thursdayOfWeek = now;
    } else if (dayOfWeek > 4) {
      // Friday (5) or Saturday (6)
      thursdayOfWeek = now.subtract(dayOfWeek - 4, 'day');
    } else {
      // Sunday (0), Monday (1), Tuesday (2), Wednesday (3)
      thursdayOfWeek = now.subtract(dayOfWeek + 3, 'day');
    }
    
    // Get the ISO week of this Thursday
    // This is our display week number
    return thursdayOfWeek.isoWeek();
  };

  const { tableProps, sorters, tableQuery: { isLoading }, searchFormProps, filters } =
    useTable<IVesselStartupKit>({
      resource: "vessel-startup-kit",
      syncWithLocation: false,

      pagination: {
        pageSize: isVesselUser ? 20 : 10
      },

      sorters: {
        permanent: [
          {
            field: "sortOrder",
            order: "asc",
          },
        ]
      }
    });
  
  const { show, visible, close } = useModal();


  const { selectProps: imoSelectProps } = useSelect<IVesselStartupKit>({
    resource: "vessel-startup-kit",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IVesselStartupKit> = [
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 150,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("title", sorters),
      sorter: true,
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 300,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("fileName", sorters),
      sorter: true,
    },
    {
      title: "File Size",
      dataIndex: "fileSize",
      key: "fileSize",
      width: 120,
      className: "wrap-text",
      render: (value) => <TextField value={value ? prettyBytes(Number(value)) : null} />,
      defaultSortOrder: getDefaultSortOrder("fileSize", sorters),
      sorter: true,
    },
    {
      title: "Last Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 200,
      className: "wrap-text",
      render: (value) => (
        <DateField format="DD-MMM-YYYY hh:mm:ss A" value={value} />
      ),
      defaultSortOrder: getDefaultSortOrder("updatedAt", sorters),
      sorter: (a: IVesselStartupKit, b: IVesselStartupKit) => {
        return dayjs(a.updatedAt).unix() - dayjs(b.updatedAt).unix();
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => (
        <Space>
          <DownloadResource
            storageUrl={record?.storageUrl}
            imoNumber={record?.imoNumber}
            ukhoLicenceId={record?.ukhoLicenceId}
            fileName={record?.fileName}
          />
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            disabled
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        breadcrumb={false}
        headerProps={{
          // onBack: () => window.history.back(),
          title: "Vessel Startup Kit",
          subTitle: "onboard new vessel",
          tags: isLoading ? (
            <Space>
              <Tag color="orange">loading</Tag>  
              <Tag color="gold">
                Update Week {getDisplayWeekNumber()}
              </Tag>
            </Space>          
          ) : (
            <Space>
              <Tag color="green">live</Tag>  
              <Tag color="gold">
                Update Week {getDisplayWeekNumber()}
              </Tag>
            </Space>
          ),
          extra: (
            <React.Fragment>
              <Space>
              <CreateButton 
                  children="Refresh Data"
                  disabled={false} 
                />
              </Space>
            </React.Fragment>
          ),
          ghost: true,
          footer: (
            <Divider plain>
              © {new Date().getFullYear()} e-Mariner NavSolutions
            </Divider>
          ),
        }}
      >
        <Row gutter={[16, 16]}>
          <Col xl={24} xs={24}>
            <Table
              {...tableProps}
              columns={columns}
              rowKey="id"
              size="middle"
              scroll={{ x: 1200 }}
              sticky
              pagination={isVesselUser ? false : {
                ...tableProps.pagination,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              }}
            />
          </Col>
        </Row>
    </List>
  </>
  );
};